// src/pages/CategoriesPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import products from '../data/products';
import './CategoriesPage.css';

const CategoriesPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all'); // Use only this one

  // Initialize categories with loading
  useEffect(() => {
    setIsLoading(true);
    const uniqueCategories = [...new Set(products.map(p => p.category))];

    // Add category stats
    const categoriesWithStats = uniqueCategories.map(category => {
      const categoryProducts = products.filter(p => p.category === category);
      const strains = [...new Set(categoryProducts.map(p => p.strain))];

      const prices = categoryProducts.flatMap(p =>
        p.priceOptions ? p.priceOptions.map(opt => opt.price) : [p.price]
      );
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      return {
        id: category.toLowerCase().replace(/\s+/g, '-'),
        name: category,
        displayName: category.replace('-', ' '),
        productCount: categoryProducts.length,
        strainCount: strains.length,
        priceRange: minPrice !== maxPrice ? `${minPrice} - ${maxPrice}` : minPrice,
        icon: getCategoryIcon(category),
        color: getCategoryColor(category),
        description: getCategoryDescription(category),
        products: categoryProducts.slice(0, 3) // Show sample products
      };
    });

    setCategories(categoriesWithStats);
    setTimeout(() => setIsLoading(false), 500);
  }, []);

  // Filter categories based on search and filter
  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || category.name === selectedFilter;

    return matchesSearch && matchesFilter;
  });

  const getCategoryIcon = (category) => {
    const icons = {
      'weekly-special': '🏷️',
      'Flower': '🌿',
      'Exotic-Flower': '🌸',
      'Edibles': '🍬',
      'Concentrates': '⚗️',
      'cart': '🔋',
      'disposable-cart': '💨',
      'Pre-rolls': '🚬',
      'Shake': '🍃'
    };
    return icons[category] || '📦';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'weekly-special': '#ef1325',
      'Flower': '#10b981',
      'Exotic-Flower': '#8b5cf6',
      'Edibles': '#f59e0b',
      'Concentrates': '#3b82f6',
      'cart': '#06b6d4',
      'disposable-cart': '#6366f1',
      'Pre-rolls': '#ef4444',
      'Shake': '#78716c'
    };
    return colors[category] || '#6b7280';
  };

  const getCategoryDescription = (category) => {
    const descriptions = {
      'weekly-special': 'Our Weekly specials',
      'Flower': 'Premium cannabis flower in various strains and quantities',
      'Exotic-Flower': 'Top-shelf exotic strains for discerning connoisseurs',
      'Edibles': 'Delicious THC-infused treats in various forms',
      'Concentrates': 'High-potency extracts for experienced users',
      'cart': '510-thread vape cartridges with premium extracts',
      'disposable-cart': 'Convenient disposable vape pens',
      'Pre-rolls': 'Perfectly rolled joints for instant enjoyment',
      'Shake': 'Premium shake at excellent value'
    };
    return descriptions[category] || 'Premium cannabis products';
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/category/${categoryId}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleHomeClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContactClick = () => {
    navigate('/contact');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewAllProducts = () => {
    navigate('/products');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Stats for header
  const totalProducts = products.length;
  const totalStrains = [...new Set(products.map(p => p.strain))].length;

  // Handle Most Popular filter click - now toggles between 'all' and 'weekly-special'
  const handleMostPopularClick = () => {
    setSelectedFilter(prev => prev === 'weekly-special' ? 'all' : 'weekly-special');
  };

  return (
    <div className="categories-page">
      {/* Hero Header */}
      <div className="categories-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <nav className="hero-breadcrumb">
            <button onClick={handleHomeClick} className="breadcrumb-link">
              <svg viewBox="0 0 24 24" fill="none" className="breadcrumb-icon">
                <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              Home
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Categories</span>
          </nav>

          <div className="hero-main">
            <h1 className="hero-title">Product Categories</h1>
            <p className="hero-subtitle">
              Explore our curated collection of premium cannabis products,
              carefully selected for quality and variety
            </p>

            <div className="hero-stats">
              <div className="hero-stat">
                <div className="stat-number">{categories.length}</div>
                <div className="stat-label">Categories</div>
              </div>
              <div className="stat-divider"></div>
              <div className="hero-stat">
                <div className="stat-number">{totalProducts}</div>
                <div className="stat-label">Total Products</div>
              </div>
              <div className="stat-divider"></div>
              <div className="hero-stat">
                <div className="stat-number">{totalStrains}</div>
                <div className="stat-label">Unique Strains</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="categories-container">
        {/* Search & Filters */}
        <div className="categories-controls">
          <div className="search-container">
            <div className="search-wrapper">
              <svg viewBox="0 0 24 24" fill="none" className="search-icon">
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="clear-search-btn"
                  aria-label="Clear search"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="filter-buttons">
            <button
              onClick={() => setSelectedFilter('all')}
              className={`filter-btn ${selectedFilter === 'all' ? 'active' : ''}`}
            >
              All Categories
            </button>
            <button
              onClick={handleMostPopularClick}
              className={`filter-btn ${selectedFilter === 'weekly-special' ? 'active' : ''}`}
            >
              Most Popular
            </button>
            <button
              onClick={handleViewAllProducts}
              className="view-all-btn"
            >
              View All Products
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Results Info */}
        <div className="results-info">
          <p className="results-count">
            {searchTerm ? (
              <>
                Found <span className="highlight">{filteredCategories.length}</span> categories matching "{searchTerm}"
              </>
            ) : selectedFilter === 'weekly-special' ? (
              <>
                Showing <span className="highlight">Weekly Specials</span> ({filteredCategories.length} category)
              </>
            ) : (
              <>
                Showing all <span className="highlight">{filteredCategories.length}</span> categories
              </>
            )}
          </p>
        </div>


        {/* Categories Grid */}
        {isLoading ? (
          <div className="loading-grid">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="category-skeleton">
                <div className="skeleton-header">
                  <div className="skeleton-icon"></div>
                  <div className="skeleton-arrow"></div>
                </div>
                <div className="skeleton-content">
                  <div className="skeleton-line"></div>
                  <div className="skeleton-line short"></div>
                </div>
                <div className="skeleton-stats">
                  <div className="skeleton-stat"></div>
                  <div className="skeleton-stat"></div>
                  <div className="skeleton-stat"></div>
                </div>
                <div className="skeleton-footer"></div>
              </div>
            ))}
          </div>
        ) : filteredCategories.length > 0 ? (
          <div className="categories-grid">
            {filteredCategories.map((category, index) => (
              <button
                key={category.id}
                onClick={() => handleCategoryClick(category.name)}
                className="category-card"
                style={{ '--category-color': category.color }}
                data-aos="fade-up"
                data-aos-delay={index * 50}
              >
                <div className="card-corner">
                  <div className="corner-decoration"></div>
                </div>

                <div className="card-header">
                  <div className="category-icon-wrapper">
                    <div className="icon-backdrop"></div>
                    <span className="category-icon">{category.icon}</span>
                  </div>
                  <div className="arrow-indicator">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>

                <div className="card-content">
                  <h3 className="category-name">{category.displayName}</h3>
                  <p className="category-description">{category.description}</p>

                  <div className="category-stats">
                    <div className="stat-item">
                      <div className="stat-icon-wrapper">
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>
                      <div className="stat-text">
                        <div className="stat-value">{category.productCount}</div>
                        <div className="stat-label">Products</div>
                      </div>
                    </div>

                    <div className="stat-item">
                      <div className="stat-icon-wrapper">
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M9.663 17h4.674M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>
                      <div className="stat-text">
                        <div className="stat-value">{category.strainCount}</div>
                        <div className="stat-label">Strains</div>
                      </div>
                    </div>

                    <div className="stat-item">
                      <div className="stat-icon-wrapper">
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>
                      <div className="stat-text">
                        <div className="stat-value">${category.priceRange}</div>
                        <div className="stat-label">From</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-footer">
                  <div className="cta-button">
                    <span>Browse Category</span>
                    <div className="cta-arrow">
                      <svg viewBox="0 0 16 16" fill="none">
                        <path d="M3 8h10m-5 5l5-5-5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Hover effect */}
                <div className="card-hover-effect"></div>
              </button>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            <h2 className="empty-state-title">No categories found</h2>
            <p className="empty-state-text">
              {searchTerm ? `No categories matching "${searchTerm}"` : 'No categories available'}
            </p>
            <div className="empty-state-actions">
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="empty-state-btn primary"
                >
                  Clear Search
                </button>
              )}
              <button
                onClick={handleViewAllProducts}
                className="empty-state-btn secondary"
              >
                View All Products
              </button>
            </div>
          </div>
        )}

        {/* Footer CTA */}
        <div className="categories-footer">
          <div className="footer-content">
            <div className="footer-icon">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0"
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            <div className="footer-text">
              <h3 className="footer-title">Need Help Choosing?</h3>
              <p className="footer-description">
                Our experts can help you find the perfect products for your needs, Text or Call (202) 476-9173
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;